import {
  BRAND_HANDLE,
  BRAND_PERSON_NAME,
  BRAND_SITE_URL,
  BRAND_OG_IMAGE_URL,
} from "@/lib/brandConfig";
import { useSEO } from "@/hooks/useSEO";
import { useLanguage } from "@/hooks/useLanguage";
import { getBaseLanguage } from "@/lib/i18n";
// Amplitude tracking removed
import { Link } from "react-router-dom";
import { Instagram, Youtube } from "lucide-react";
import profileImage from "@/assets/profile.jpg";

const socialLinks = [
  { icon: Instagram, label: "Instagram", href: `https://instagram.com/${BRAND_HANDLE}` },
  { icon: Youtube, label: "YouTube", href: `https://www.youtube.com/@${BRAND_HANDLE}` },
];

const ContentCreator = () => {
  const seoConfig = {
    title: `${BRAND_PERSON_NAME} | Content Creator - Professional Profile`,
    description: "Professional content creator specializing in lifestyle, nutrition, beauty and travel content. Connect with authentic and inspiring content that resonates with thousands.",
    keywords: `${BRAND_PERSON_NAME}, content creator, professional profile, lifestyle content, nutrition, beauty, travel, influencer, social media, authentic content`,
    ogImage: BRAND_OG_IMAGE_URL,
    canonical: `${BRAND_SITE_URL}/content-creator`,
    robots: "index, follow, max-snippet:-1, max-image-preview:large",
    type: "profile",
    author: BRAND_PERSON_NAME,
    modifiedTime: new Date().toISOString(),
    locale: "en_US",
    alternateLocale: "es_AR"
  };
  
  useSEO(seoConfig);
  const { language } = useLanguage();

  return (
    <div className="min-h-screen bg-background">
      <main className="max-w-md mx-auto px-5 py-12 md:py-16">
        <header className="text-center mb-10 animate-fade-in">
          <div className="relative inline-block mb-8">
            <div className="absolute -inset-1 rounded-full bg-gradient-to-br from-border via-muted to-border opacity-60" />
            <div className="relative w-28 h-28 rounded-full overflow-hidden ring-2 ring-background shadow-lg">
              <img
                src={profileImage}
                alt={`${BRAND_PERSON_NAME} - Content Creator portrait photo`}
                loading="lazy"
                width={112}
                height={112}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute inset-0 rounded-full bg-gradient-to-tr from-transparent via-background/10 to-transparent pointer-events-none" />
          </div>

          <h1 className="text-3xl md:text-4xl font-display font-medium text-foreground tracking-wide mb-3 italic">
            Content Creator
          </h1>
          <div className="flex items-center justify-center gap-2 mb-5">
            <span className="w-6 h-px bg-border" />
            <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-medium">
              {BRAND_PERSON_NAME}
            </p>
            <span className="w-6 h-px bg-border" />
          </div>
        </header>

        <section className="mb-10 animate-fade-in" style={{ animationDelay: "0.1s" }}>
          <div className="prose prose-sm max-w-none text-muted-foreground space-y-4 text-center">
            <p className="text-sm leading-relaxed">
              {getBaseLanguage(language) === "es" 
                ? "Soy <strong>modelo de Buenos Aires</strong>, 18 años, apasionada por las <strong>redes sociales y el crecimiento</strong>. Comparto lifestyle, belleza y bienestar en Instagram, TikTok y YouTube."
                : "I'm a <strong>model from Buenos Aires</strong>, 18, passionate about <strong>social media and growth</strong>. I share lifestyle, beauty and wellness on Instagram, TikTok and YouTube."
              }
            </p>
            <p className="text-sm leading-relaxed">
              {getBaseLanguage(language) === "es"
                ? "Mi contenido refleja mi evolución: campañas, día a día y una comunidad que crece conmigo."
                : "My content reflects my evolution: campaigns, everyday moments and a community growing with me."
              }
            </p>
          </div>
        </section>

        <section className="mb-10 animate-fade-in" style={{ animationDelay: "0.2s" }}>
          <h2 className="text-[9px] uppercase tracking-[0.25em] text-muted-foreground text-center mb-6 font-medium">
            Follow Me
          </h2>
          <div className="flex justify-center gap-6">
            {socialLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 flex items-center justify-center rounded-full border border-border text-muted-foreground hover:text-foreground hover:border-foreground/30 transition-all duration-300"
                aria-label={link.label}
              >
                <link.icon className="w-4 h-4" strokeWidth={1.5} />
              </a>
            ))}
          </div>
        </section>

        <div className="flex items-center justify-center gap-3 mb-10">
          <span className="w-12 h-px bg-border" />
          <span className="w-1 h-1 bg-muted-foreground/40 rotate-45" />
          <span className="w-12 h-px bg-border" />
        </div>

        <section className="mb-10 animate-fade-in" style={{ animationDelay: "0.3s" }}>
          <h2 className="text-[9px] uppercase tracking-[0.25em] text-muted-foreground text-center mb-6 font-medium">
            Topics
          </h2>
          <div className="grid grid-cols-2 gap-4">
            {[
              { name: "Lifestyle", href: "/lifestyle" },
              { name: "Wellbeing", href: "/nutricion" },
              { name: "Beauty", href: "/belleza" },
              { name: "Travel", href: "/viajes" },
            ].map((topic) => (
              <Link
                key={topic.name}
                to={topic.href}
                className="p-4 text-center border border-border rounded hover:border-foreground/30 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-foreground/20"
              >
                <span className="text-xs tracking-wide text-muted-foreground hover:text-foreground">
                  {topic.name}
                </span>
              </Link>
            ))}
          </div>
        </section>

        <div className="text-center animate-fade-in" style={{ animationDelay: "0.4s" }}>
          <Link
            to="/"
            onClick={() => {/* trackInternalLinkClick removed */}}
            className="inline-block text-xs uppercase tracking-[0.2em] text-muted-foreground hover:text-foreground transition-colors focus:outline-none focus:ring-2 focus:ring-foreground/20 rounded"
          >
              Back to Home
          </Link>
        </div>

        <footer className="mt-14 text-center animate-fade-in" style={{ animationDelay: "0.5s" }}>
          <p className="text-[9px] uppercase tracking-[0.2em] text-muted-foreground/60">
            {`@${BRAND_HANDLE}`}
          </p>
        </footer>
      </main>
    </div>
  );
};

export default ContentCreator;


